<template>
    <v-app>
        <v-container fluid>
            <template v-if="$insertAccess($route.path)">
                <v-form @submit.prevent="submit">
                    <v-row>
                        <v-col cols="12">
                            <v-expansion-panels v-model="panel">
                                <v-expansion-panel>
                                    <v-expansion-panel-title color="blue-grey-lighten-5"
                                        class="panel-header font-weight-medium text-primary">
                                        <v-icon start color="primary" class="mr-2">mdi-cash-multiple</v-icon>
                                        <span>{{ isEntryMode ? "Add Project Budget" : "Update Project Budget" }}</span>
                                    </v-expansion-panel-title>

                                    <v-expansion-panel-text>
                                        <!-- All Forms in Same Row -->
                                        <v-row class="py-2">
                                            <v-col lg="3" md="3" cols="12">
                                                <v-autocomplete density="compact" variant="outlined"
                                                    bg-color="grey-lighten-4" class="px-2"
                                                    v-model="project_id.value.value"
                                                    :error-messages="project_id.errorMessage.value" clearable
                                                    :items="getProjectList"
                                                    :item-title="item => item ? `${item.code} - ${item.project_title}` : ''"
                                                    autocomplete="off" item-value="id"
                                                    @update:model-value="onProjectChange">
                                                    <template #label>
                                                        Project Name <span class="text-red-accent-4">*</span>
                                                    </template>
                                                </v-autocomplete>
                                            </v-col>

                                            <v-col lg="3" md="3" cols="12">
                                                <v-text-field v-model="project_code.value.value" density="compact"
                                                    variant="outlined" bg-color="grey-lighten-4" class="px-2"
                                                    :error-messages="project_code.errorMessage.value" readonly>
                                                    <template #label>
                                                        Project Code
                                                    </template>
                                                </v-text-field>
                                            </v-col>

                                            <v-col lg="3" md="3" cols="12">
                                                <v-text-field v-model="budget_amount.value.value" density="compact"
                                                    variant="outlined" bg-color="grey-lighten-4" class="px-2"
                                                    type="number" step="0.01"
                                                    :error-messages="budget_amount.errorMessage.value" clearable>
                                                    <template #label>
                                                        Project Total Budget <span class="text-red-accent-4">*</span>
                                                    </template>
                                                </v-text-field>
                                            </v-col>

                                            <v-col lg="3" md="3" cols="12">
                                                <v-checkbox
                                                    label="Active"
                                                    v-model="status.value.value"
                                                    :error-messages="status.errorMessage.value"
                                                    density="compact"
                                                    variant="outlined"
                                                    hide-details
                                                    single-line
                                                    color="primary"
                                                    value="1"
                                                ></v-checkbox>
                                            </v-col>
                                        </v-row>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-col>
                    </v-row>

                    <v-card-actions>
                        <v-layout class="justify-end">
                            <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                                {{ isEntryMode ? "Save" : "Update" }}
                            </v-btn>
                        </v-layout>
                    </v-card-actions>
                </v-form>
            </template>

            <!-- Project Budget List Table -->
            <v-row class="mt-4">
                <v-col cols="12">
                    <v-expansion-panels v-model="listPanel">
                        <v-expansion-panel>
                            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">Project Budget List</v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <v-row class="py-2">
                                    <v-col md="4" cols="12">
                                        <v-text-field v-model="search" append-inner-icon="mdi-magnify" label="Search"
                                            single-line hide-details density="compact" variant="outlined"
                                            bg-color="grey-lighten-4" class="pl-2" clearable></v-text-field>
                                    </v-col>

                                    <v-col md="5" cols="12">
                                        <div class="d-flex align-center flex-column">
                                            <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success"
                                                    @click="exportToCSV">CSV</v-btn>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="primary"
                                                    @click="exportToExcel">EXCEL</v-btn>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red"
                                                    @click="exportToPDF">PDF</v-btn>
                                            </v-btn-group>
                                        </div>
                                    </v-col>

                                    
                                </v-row>

                                <v-data-table :headers="headers" :items="getProjectBudgetList" :loading="loading"
                                    v-model:items-per-page="itemsPerPage" :search="search" :hover="true"
                                    density="compact">

                                    <template v-slot:item.index="{ index }">
                                        {{ ++index }}
                                    </template>

                                    <template v-slot:item.budget_amount="{ item }">
                                        {{ formatCurrency(item.budget_amount) }}
                                    </template>

                                    <template v-slot:item.status="{ item }">
                                        <v-chip v-if="item.status == 1" color="success" size="small"
                                            class="text-capitalize">Active</v-chip>
                                        <v-chip v-else color="red" size="small"
                                            class="text-capitalize">Inactive</v-chip>
                                    </template>

                                    <template v-slot:item.approved_date="{ item }">
                                        {{ formattedDate(item.approved_date) }}
                                    </template>

                                    <template v-slot:item.actions="{ item }">
                                        <v-icon class="me-2 edit-button" color="primary" @click="editItem(item)"
                                            v-if="$updateAccess($route.path)">
                                            mdi mdi-pencil-outline
                                        </v-icon>
                                        <v-icon class="me-2 edit-button" color="red" @click="deleteItem(item)"
                                            v-if="$updateAccess($route.path)">
                                            mdi mdi-delete-outline
                                        </v-icon>
                                    </template>
                                </v-data-table>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-container>
    </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { getCurrentInstance, ref, reactive } from "vue";
import { toast } from "vue3-toastify";
import { useRouter } from "vue-router";
import formattedDate from "@/plugins/dateFormatter";

export default {
    data: () => ({
        panel: [0],
        listPanel: [0],
        isEntryMode: true,
        itemsPerPage: 50,
        loading: false,
        search: "",
        headers: [
            { title: "#SL", align: "center", key: "index", width: "7%", sortable: false },
            { title: "Project Budget ID", key: "id", align: "left", sortable: false },
            { title: "Project Name", key: "project_name", align: "left", sortable: false },
            { title: "Project Code", key: "project_code", align: "left", sortable: false },
            { title: "Project Total Budget", key: "budget_amount", align: "right", sortable: false },
            { title: "Status", align: "center", key: "status", sortable: true },
            { title: "Approved By", key: "approved_by", align: "left", sortable: false },
            { title: "Approved Date", key: "approved_date", align: "left", sortable: false },
            { title: "Actions", align: "center", key: "actions", sortable: false }
        ],
    }),

    watch: {
        '$route.query': {
            handler() {
                this.loadData()
            },
            deep: true,
            immediate: false
        },
    },

    created() {
        if (this.$store.getters.getProjectList.length === 0) {
            this.$store.dispatch("getProjectList");
        }
        if (this.$store.getters.getProjectBudgetList.length === 0) {
            this.loading = true;
            this.$store.dispatch('getProjectBudgetList').then((response) => {
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        }
    },

    computed: {
        getProjectList() {
            return this.$store.getters.getProjectList;
        },
        getProjectBudgetList() {
            return this.$store.getters.getProjectBudgetList;
        },
    },

    methods: {
        formattedDate,
        onProjectChange(projectId) {
            if (!projectId) {
                this.project_code.value.value = "";
                return;
            }
            const projectList = this.getProjectList || [];
            const selectedProject = projectList.find(project => project.id == projectId);
            if (selectedProject) {
                this.project_code.value.value = selectedProject.code;
            } else {
                this.project_code.value.value = "";
            }
        },
        loadData() {
            const queryId = this.$route.query.id;
            if (queryId) {
                // TODO: Implement edit mode when we have getProjectBudgetById
                this.isEntryMode = false;
                // For now, just reset form for edit mode
                this.budget_id.value.value = queryId;
                // Set default status if not already set
                if (!this.status.value.value) {
                    this.status.setValue("1", false);
                }
            } else {
                this.isEntryMode = true;
                this.resetForm();
                // Set default status for new entries
                this.status.setValue("1", false);
            }
        },
        goToAddMode() {
            this.isEntryMode = true;
            this.resetForm();
            this.status.setValue("1", false); // Set default status for new entries
            this.panel = [0]; // Open the form panel
        },
        formatCurrency(amount) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(amount || 0);
        },
        editItem(item) {
            // TODO: Implement edit functionality when we have getProjectBudgetById API
            this.isEntryMode = false;
            this.resetForm({ errors: {}, touched: {} });
            this.budget_id.setValue(item.id || "", false);
            this.project_id.setValue(item.project_id || "", false);
            this.project_code.setValue(item.project_code || "", false);
            this.budget_amount.setValue(item.budget_amount || "", false);
            this.status.setValue(String(item.status) || "1", false);
            this.panel = [0]; // Open the form panel
        },
        deleteItem(item) {
            if (confirm('Are you sure you want to delete this project budget?')) {
                // TODO: Implement delete functionality when we have delete API
                toast.info('Delete functionality will be implemented when API is available');
            }
        },
        exportToPDF() {
            let serialNumber = 1;
            const columns = ['#SL', 'Project Budget ID', 'Project Name', 'Project Code', 'Project Total Budget', 'Status', 'Approved By', 'Approved Date'];
            const rows = this.getProjectBudgetList.map(item => [
                serialNumber++,
                item.id,
                item.project_name,
                item.project_code,
                this.formatCurrency(item.budget_amount),
                item.status == 1 ? 'Active' : 'Inactive',
                item.approved_by || '',
                this.formattedDate(item.approved_date)
            ]);
            this.$exportTableToPDF(columns, rows, 'Project Budget List', 'project_budget_list_' + this.$fileDate())
        },
        exportToExcel() {
            var data = [['#SL', 'Project Budget ID', 'Project Name', 'Project Code', 'Project Total Budget', 'Status', 'Approved By', 'Approved Date']];
            var serialNumber = 1;
            this.getProjectBudgetList.forEach(item => {
                data.push([
                    serialNumber++,
                    item.id,
                    item.project_name,
                    item.project_code,
                    item.budget_amount,
                    item.status == 1 ? 'Active' : 'Inactive',
                    item.approved_by || '',
                    this.formattedDate(item.approved_date)
                ]);
            });
            this.$exportTableToExcel(data, 'project_budget_list_' + this.$fileDate());
        },
        exportToCSV() {
            var data = [['#SL', 'Project Budget ID', 'Project Name', 'Project Code', 'Project Total Budget', 'Status', 'Approved By', 'Approved Date']];
            var serialNumber = 1;
            this.getProjectBudgetList.forEach(item => {
                data.push([
                    serialNumber++,
                    item.id,
                    item.project_name,
                    item.project_code,
                    item.budget_amount,
                    item.status == 1 ? 'Active' : 'Inactive',
                    item.approved_by || '',
                    this.formattedDate(item.approved_date)
                ]);
            });
            this.$exportTableToCSV(data, 'project_budget_list_' + this.$fileDate());
        },
    },

    setup() {
        const { handleSubmit, resetForm } = useForm({
            validationSchema: yup.object({
                project_id: yup.number().required().label("Project Name"),
                project_code: yup.string().nullable().label("Project Code"),
                budget_amount: yup.number().required().min(0).label("Project Total Budget"),

                status: yup.string().nullable(),
            }),
        });

        const project_id = useField("project_id");
        const project_code = useField("project_code");
        const budget_amount = useField("budget_amount");
        const allocated_amount = useField("allocated_amount");
        const remaining_amount = useField("remaining_amount");
        const remarks = useField("remarks");
        const status = useField("status");
        const budget_id = useField('budget_id');

        const store = useStore();
        const router = useRouter();

        const submit = handleSubmit((values) => {
            const loading = ref(true);

            store
                .dispatch("saveUpdateProjectBudget", values)
                .then((response) => {
                    resetForm();
                    loading.value = false;
                    toast.success(response.data.message || "Project budget saved successfully", {
                        timeout: 3000,
                    });
                    setTimeout(() => {
                        store.dispatch("getProjectBudgetList");
                        router.push({ name: "project-budget" });
                    }, 2000);
                })
                .catch((error) => {
                    loading.value = false;
                    console.error("Error:", error);
                    toast.error(error.response?.data?.message || "An error occurred");
                });
        });

        return {
            submit,
            project_id,
            project_code,
            budget_amount,
            allocated_amount,
            remaining_amount,
            remarks,
            status,
            budget_id,
            resetForm,
        };
    },

    mounted() {
        this.loadData()
    },
}
</script>